/**
 * Pure helper that decides which incoming files we can accept,
 * given the files already attached and the configured limits.
 *
 * Returns `{ accepted, rejected }` where:
 *   - accepted: File[] — files that passed every check.
 *   - rejected: Array<{ file: File, reason: string }> — per-file
 *     reason matches one of FILE_REJECT_REASONS so the caller can
 *     render a localized message.
 */

export const FILE_REJECT_REASONS = {
  MIME: 'mime',
  SIZE: 'size',
  COUNT: 'count',
  TOTAL_SIZE: 'totalSize',
};

const sumBytes = files => files.reduce((acc, f) => acc + (f.size || 0), 0);

const passesMime = (file, acceptedPrefixes) =>
  acceptedPrefixes.some(prefix => (file.type || '').startsWith(prefix));

export const validateFiles = ({ incoming, existing, limits }) => {
  const accepted = [];
  const rejected = [];

  // Track running totals so each decision sees the up-to-date state.
  let runningCount = existing.length;
  let runningBytes = sumBytes(existing);

  for (const file of incoming) {
    if (!passesMime(file, limits.acceptedMimePrefixes)) {
      rejected.push({ file, reason: FILE_REJECT_REASONS.MIME });
      continue;
    }
    if (file.size > limits.maxFileBytes) {
      rejected.push({ file, reason: FILE_REJECT_REASONS.SIZE });
      continue;
    }
    if (runningCount + 1 > limits.maxFiles) {
      rejected.push({ file, reason: FILE_REJECT_REASONS.COUNT });
      continue;
    }
    if (runningBytes + file.size > limits.maxTotalBytes) {
      rejected.push({ file, reason: FILE_REJECT_REASONS.TOTAL_SIZE });
      continue;
    }

    accepted.push(file);
    runningCount += 1;
    runningBytes += file.size;
  }

  return { accepted, rejected };
};
